/**
 * Forced2FAGate — globally mounted overlay that nags (or hard-blocks) any
 * super_admin who hasn't enrolled in 2FA yet.
 *
 * Behaviour:
 *   • super_admin still inside the grace period → soft amber banner at the top.
 *   • super_admin past the grace period (`required: true`) → full-page modal
 *     covering the UI with a single "Ativar 2FA agora" CTA pointing to /account.
 *   • Anyone else (or already-enrolled) → renders nothing.
 *
 * Polls /api/auth/2fa/policy on mount + when the route changes so a freshly
 * enrolled user immediately sees the gate disappear.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { ShieldAlert, ShieldCheck, X } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "alp.2fa.policy.dismiss_until";

export default function Forced2FAGate() {
  const { user } = useAuth();
  const location = useLocation();
  const [policy, setPolicy] = useState(null);
  const [dismissedUntil, setDismissedUntil] = useState(() => {
    try { return Number(localStorage.getItem(DISMISS_KEY) || "0"); } catch { return 0; }
  });

  // Refetch policy whenever the user or path changes — covers post-login,
  // post-enrolment, and impersonation/end-impersonation flows.
  useEffect(() => {
    let cancelled = false;
    if (!user || user.role !== "super_admin") {
      setPolicy(null);
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/2fa/policy`);
        if (!cancelled) setPolicy(data);
      } catch { if (!cancelled) setPolicy(null); }
    })();
    return () => { cancelled = true; };
  }, [user, location.pathname]);

  if (!user || user.role !== "super_admin" || !policy) return null;
  if (policy.enabled) return null;
  // Don't double-overlay the change-password screen or the magic-login bridge.
  if (["/change-password", "/magic-login", "/auth/callback"].includes(location.pathname)) return null;

  // Hard block — overdue
  if (policy.required) {
    return (
      <div className="fixed inset-0 z-[2147483600] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
           data-testid="forced-2fa-block">
        <div className="bg-[#0F0820] border border-rose-500/40 rounded-2xl p-7 max-w-md w-full text-white shadow-2xl">
          <div className="w-12 h-12 rounded-xl bg-rose-500/15 text-rose-300 flex items-center justify-center mb-4">
            <ShieldAlert className="w-6 h-6" />
          </div>
          <h2 className="text-[18px] font-extrabold mb-1.5">2FA obrigatório para Super Admin</h2>
          <p className="text-[13px] text-white/70 leading-relaxed mb-4">
            Sua conta tem privilégios de Master há <b>{policy.days_since_account} dias</b> e o
            limite de tolerância é <b>{policy.grace_period_days} dias</b>. Antes de continuar,
            ative a autenticação em dois fatores na página <b>Account</b>.
          </p>
          <div className="rounded-lg border border-white/10 bg-white/[0.04] p-3 mb-5 text-[12px] text-white/65">
            • Use Google Authenticator, 1Password ou Authy<br />
            • Você receberá 8 códigos de recuperação para emergência<br />
            • A configuração leva menos de 60 segundos
          </div>
          <Link to="/account" data-testid="forced-2fa-cta"
            className="block w-full text-center h-11 rounded-lg bg-[#C9A24E] hover:bg-[#b48d3e] text-[#1A0E36] font-extrabold text-[13px] inline-flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4" /> Ativar 2FA agora
          </Link>
        </div>
      </div>
    );
  }

  // Soft warn — within grace period
  if (!policy.recommended) return null;
  const now = Date.now();
  if (dismissedUntil && now < dismissedUntil) return null;

  const dismiss = () => {
    const until = now + 24 * 60 * 60 * 1000; // remute 24h
    try { localStorage.setItem(DISMISS_KEY, String(until)); } catch { /* noop */ }
    setDismissedUntil(until);
  };

  const overdueIn = Math.max(0, policy.days_until_required);
  return (
    <div className="fixed top-0 left-0 right-0 z-[2147483500] bg-amber-500 text-amber-950 px-4 py-2.5 flex items-center justify-center gap-3 text-[12.5px] font-bold shadow-md"
         data-testid="forced-2fa-soft-banner">
      <ShieldAlert className="w-4 h-4 shrink-0" />
      <span className="truncate">
        Ative o 2FA na sua conta Super Admin nos próximos <b>{overdueIn} dia{overdueIn === 1 ? "" : "s"}</b>.
        Após esse prazo o acesso será bloqueado até a configuração.
      </span>
      <Link to="/account" className="px-3 py-1 rounded bg-amber-950 text-amber-50 hover:bg-black"
            data-testid="forced-2fa-soft-cta">
        Ativar agora →
      </Link>
      <button onClick={dismiss} className="text-amber-950/75 hover:text-amber-950"
              title="Lembrar em 24h" data-testid="forced-2fa-dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
